import { UserRole } from "@features/settings/types/settings.types";
import { Button } from "@shared/components/ui/Button";
import { useProfile } from "@shared/hooks/useProfile";
import AuthenticatedLayout from "@shared/layouts/AuthenticatedLayout";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useResourcesStore } from "../store/useResourcesStore";
import { Resource, ResourceApprovalStatus } from "../types/resource.types";

const STATUS_LABELS: Record<ResourceApprovalStatus, string> = {
  [ResourceApprovalStatus.PENDING]: "Pending",
  [ResourceApprovalStatus.APPROVED]: "Approved",
  [ResourceApprovalStatus.REJECTED]: "Rejected",
};

const STATUS_CLASSES: Record<ResourceApprovalStatus, string> = {
  [ResourceApprovalStatus.PENDING]:
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
  [ResourceApprovalStatus.APPROVED]:
    "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  [ResourceApprovalStatus.REJECTED]:
    "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
};

interface CommentTooltipProps {
  comment: string;
}

const CommentTooltip: React.FC<CommentTooltipProps> = ({ comment }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      {/* Clickable single-line preview */}
      <span
        className="block w-full truncate text-gray-600 dark:text-gray-300 text-sm cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
        onClick={() => setOpen(true)}
        title={comment}
      >
        {comment}
      </span>

      {/* Modal overlay */}
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md mx-4 flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Blue header */}
            <div className="flex items-center justify-between px-5 py-4 bg-blue-600">
              <h3 className="text-base font-semibold text-white">Comments</h3>
              <button
                onClick={() => setOpen(false)}
                className="text-white/80 hover:text-white transition-colors"
                aria-label="Close"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Scrollable body */}
            <div className="px-5 py-4 overflow-y-auto max-h-60">
              <p className="text-sm text-gray-700 dark:text-gray-200 leading-relaxed whitespace-pre-wrap">
                {comment}
              </p>
            </div>

            {/* Footer */}
            <div className="flex justify-end px-5 py-3 border-t border-gray-200 dark:border-gray-700">
              <Button
                size="sm"
                variant="primary"
                className="px-6"
                onClick={() => setOpen(false)}
              >
                Accept
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const MyResourcesPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useProfile();
  const {
    myResources,
    totalMyResources,
    myCurrentPage,
    isLoadingMyResources,
    myResourcesError,
    fetchMyResources,
  } = useResourcesStore();

  const limit = 20;

  useEffect(() => {
    fetchMyResources(1, limit);
  }, [fetchMyResources]);

  useEffect(() => {
    if (user && user.role === UserRole.ADMIN) {
      navigate("/resources/management");
    }
  }, [user, navigate]);

  const formatDate = (ts: any): string => {
    if (!ts) return "—";
    const date = ts?.toDate ? ts.toDate() : new Date(ts);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleViewResource = (resource: Resource) => {
    window.open(`/resources/${resource.id}`, "_blank");
  };

  const totalPages = Math.ceil(totalMyResources / limit);

  return (
    <AuthenticatedLayout
      pageHeader={{
        title: "My Resources",
        actions: [
          ...(myResources.length === 0
            ? [
                {
                  label: "Add Resource",
                  onClick: () => navigate("/resources?create=1"),
                  variant: "orange" as const,
                },
              ]
            : []),
          {
            label: "View All Resources",
            onClick: () => navigate("/resources"),
            variant: "primary" as const,
          },
        ],
        search: { show: false },
      }}
      isLoading={isLoadingMyResources}
      error={myResourcesError ? new Error(myResourcesError) : null}
    >
      <div className="px-4 sm:px-6 lg:px-8 py-4 w-full max-w-9xl mx-auto">
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
          Below you can track all the resources you have submitted, along with
          their current review status and any feedback from administrators.
        </p>

        <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="px-4 py-3 whitespace-nowrap">Created</th>
                  <th className="px-4 py-3 max-w-[220px]">Title</th>
                  <th className="px-4 py-3 whitespace-nowrap">Action Date</th>
                  <th className="px-4 py-3 max-w-[200px]">Comments</th>
                  <th className="px-4 py-3 whitespace-nowrap">Status</th>
                  <th className="px-4 py-3 whitespace-nowrap">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {myResources.length === 0 && !isLoadingMyResources && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-10 text-center text-gray-400 dark:text-gray-500"
                    >
                      You haven't submitted any resources yet.
                    </td>
                  </tr>
                )}
                {myResources.map((resource) => (
                  <tr
                    key={resource.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                  >
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600 dark:text-gray-300 text-sm">
                      {formatDate(resource.createdAt)}
                    </td>
                    <td className="px-4 py-3 max-w-[220px]">
                      <span
                        className="block truncate text-gray-800 dark:text-gray-200 font-medium"
                        title={resource.title}
                      >
                        {resource.title}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600 dark:text-gray-300 text-sm">
                      {formatDate(resource.reviewedAt)}
                    </td>
                    <td className="px-4 py-3 max-w-[200px]">
                      {resource.reviewComment ? (
                        <CommentTooltip comment={resource.reviewComment} />
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500 text-sm">
                          —
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_CLASSES[resource.approvalStatus] || STATUS_CLASSES[ResourceApprovalStatus.PENDING]}`}
                      >
                        {STATUS_LABELS[resource.approvalStatus] || "Pending"}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => handleViewResource(resource)}
                      >
                        View Resource
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex justify-between items-center px-4 py-3 border-t border-gray-200 dark:border-gray-700">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Page {myCurrentPage} of {totalPages} · {totalMyResources} total
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => fetchMyResources(myCurrentPage - 1, limit)}
                  disabled={myCurrentPage <= 1}
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => fetchMyResources(myCurrentPage + 1, limit)}
                  disabled={myCurrentPage >= totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AuthenticatedLayout>
  );
};

export default MyResourcesPage;
